// MA-681 — what a sound-box item (ms.read.04 / ms.read.07 encodingWords
// `sound_box_encode` records) shows and says. Decision: ADR-055's MA-681
// addendum. The two skills DIFFER, on purpose:
//   ms.read.07 (CVC encoding, spelling) — the child hears the BARE WORD as
//     dictation (auto-spoken, replayable); the word is hidden until answered.
//     Its word clips already exist, so there is no serving gate.
//   ms.read.04 — the child hears a framed segmented cue ("Listen. kuh ... aa
//     ... tuh"), no blended-word tail; the word is hidden until answered. The
//     cue is a NEW string: it is spoken only once its recorded take exists in
//     the manifest ("narrated before it is served"); until then that item
//     keeps today's behaviour (word shown + spoken, itself a recorded clip).
// Every other sound-box skill (ms.read.05/08/09 records) is untouched.
import { buildSegmentedSoundOut } from "./decodeUtterance";

/** The neutral on-screen line. No word; no token is a lone tile letter. */
export const SOUND_BOX_CUE_DISPLAY_PROMPT = "Listen, then build the word.";

/** The framing token of the ms.read.04 cue: the authored cue began "Listen:",
 *  and the recorded house prompts open with "Listen" ("Listen, tap."). */
export const SOUND_BOX_CUE_FRAME = "Listen.";

export type SoundBoxCueKind = "segmented" | "dictation";

/** Which skills get the new surface, and which kind. Others: legacy. */
export const SOUND_BOX_CUE_KIND_BY_SKILL: Readonly<Record<string, SoundBoxCueKind>> = Object.freeze({
  "ms.read.04": "segmented",
  "ms.read.07": "dictation",
});

/** The ms.read.04 spoken cue for a record's authored grapheme list. */
export function soundBoxCueFor(graphemes: readonly string[]): string {
  return `${SOUND_BOX_CUE_FRAME} ${buildSegmentedSoundOut(graphemes)}`;
}

/** No sound-box picture asset exists anywhere in the repo (ADR-055's MA-596
 *  addendum: the `pic_*` refs resolve to nothing). A record's `PIC` hint is
 *  offered only for a ref listed here — empty today, so the dead "Picture"
 *  tab is withheld on every served sound-box item. */
export const SOUND_BOX_PICTURE_ASSETS: ReadonlySet<string> = new Set<string>();
export function soundBoxPictureResolves(ref: string | undefined | null): boolean {
  return typeof ref === "string" && SOUND_BOX_PICTURE_ASSETS.has(ref);
}

/** "pending" — manifest verdict not in yet; "cue" — the cue's clip exists;
 *  "word" — it does not (or the manifest never loaded): today's behaviour. */
export type SoundBoxCueMode = "pending" | "cue" | "word";

export interface SoundBoxSurfaceItem {
  prompt: string;
  answer: unknown;
  spokenPrompt?: string | null;
  soundBoxCueKind?: SoundBoxCueKind;
}

export interface SoundBoxSurface {
  /** The heading text. */
  shown: string;
  /** What the speaker button speaks (and auto-speaks); null = no speaker yet. */
  spoken: string | null;
  /** Auto-speak on mount. */
  autoSpeak: boolean;
  /** Whether a speaker use marks the slot read-aloud-assisted (audio-led: no). */
  markReadAloud: boolean;
  /** The single instruction line above the tiles ("" = none). */
  instruction: "" | "Spell the word";
  /** Tiles locked: a gated cue verdict is still pending (no mid-build flip). */
  tilesLocked: boolean;
}

/** The manifest-gated text: only an ms.read.04 segmented cue needs a clip
 *  check; dictation (the bare word) is already recorded. null = no gate. */
export function soundBoxGateText(item: SoundBoxSurfaceItem): string | null {
  return item.soundBoxCueKind === "segmented" && typeof item.spokenPrompt === "string" && item.spokenPrompt.trim() !== ""
    ? item.spokenPrompt
    : null;
}

/** `answered` = this item's own check/reveal happened (see slotAnswered).
 *  `legacyAutoSpeak` = what the screen auto-speaks today (word mode only). */
export function soundBoxSurface(
  item: SoundBoxSurfaceItem,
  mode: SoundBoxCueMode,
  answered: boolean,
  legacyAutoSpeak: boolean,
): SoundBoxSurface {
  const word = String(item.answer ?? "");
  const kind = item.soundBoxCueKind;
  const spokenPrompt = typeof item.spokenPrompt === "string" && item.spokenPrompt.trim() !== "" ? item.spokenPrompt : null;
  const legacy: SoundBoxSurface = {
    shown: word || item.prompt, spoken: word || item.prompt, autoSpeak: legacyAutoSpeak, markReadAloud: true, instruction: "Spell the word", tilesLocked: false,
  };
  if (!kind || spokenPrompt === null) return legacy;
  const shown = answered ? word : item.prompt;
  if (kind === "dictation") {
    return { shown, spoken: spokenPrompt, autoSpeak: true, markReadAloud: false, instruction: "", tilesLocked: false };
  }
  if (mode === "word") return legacy;
  if (mode === "pending") return { shown, spoken: null, autoSpeak: false, markReadAloud: false, instruction: "", tilesLocked: true };
  return { shown, spoken: spokenPrompt, autoSpeak: true, markReadAloud: false, instruction: "", tilesLocked: false };
}

/** MUST-2 — "answered" belongs to ONE slot. `answeredAtSlot` is the session
 *  slot index (`done`) at which the child answered/revealed (or null). The
 *  first render of the NEXT slot, before any effect clears feedback, must not
 *  count as answered. */
export function slotAnswered(answeredAtSlot: number | null, currentSlot: number): boolean {
  return answeredAtSlot !== null && answeredAtSlot === currentSlot;
}

/** The manifest-gate decision, pure. */
export function cueModeForVerdict(
  verdict: "clip" | "absent" | "unloaded",
  retriesUsed: number,
  maxRetries: number,
): SoundBoxCueMode | "retry" {
  if (verdict === "clip") return "cue";
  if (verdict === "absent") return "word";
  return retriesUsed >= maxRetries ? "word" : "retry";
}

export interface CueResolveCache {
  /** True once a full retry budget elapsed with the manifest still unloaded —
   *  later items then fall back at once instead of waiting again. */
  exhausted: boolean;
}

/** The hook's resolve loop, exported so it is executed by tests against the
 *  REAL narration source. Returns the resolved mode, or null if cancelled. */
export async function resolveSoundBoxCueMode(
  gateText: string,
  clipState: (text: string) => Promise<"clip" | "absent" | "unloaded">,
  opts: {
    sleep: (ms: number) => Promise<void>;
    isCancelled: () => boolean;
    cache: CueResolveCache;
    maxRetries: number;
    retryMs: number;
  },
): Promise<SoundBoxCueMode | null> {
  for (let n = 0; ; n++) {
    const verdict = await clipState(gateText);
    if (opts.isCancelled()) return null;
    if (verdict !== "unloaded") opts.cache.exhausted = false;
    const next = cueModeForVerdict(verdict, opts.cache.exhausted ? opts.maxRetries : n, opts.maxRetries);
    if (next !== "retry") {
      if (verdict === "unloaded") opts.cache.exhausted = true;
      return next;
    }
    await opts.sleep(opts.retryMs);
    if (opts.isCancelled()) return null;
  }
}

/** The hook's per-item selection, pure: a verdict recorded for a DIFFERENT
 *  item id never applies to this one (it is "pending" until its own verdict). */
export function modeForItem(
  state: { id: string; mode: SoundBoxCueMode },
  itemId: string,
): SoundBoxCueMode {
  return state.id === itemId ? state.mode : "pending";
}

/** The hook's returned mode, pure: no gate text -> "word"; else this item's
 *  own verdict (modeForItem). */
export function selectSoundBoxCueMode(
  gateText: string | null,
  state: { id: string; mode: SoundBoxCueMode },
  itemId: string,
): SoundBoxCueMode {
  return gateText === null ? "word" : modeForItem(state, itemId);
}

/** The hook's effect body, exported so it is EXECUTED by tests (RED r2): runs
 *  the resolve loop for `gateText` and records `{id: itemId, mode}` through
 *  `setState` — "pending" first, then the verdict — never after cleanup.
 *  Returns the cleanup. gateText null -> nothing runs. */
export function startSoundBoxCueResolve(args: {
  gateText: string | null;
  itemId: string;
  clipState: (text: string) => Promise<"clip" | "absent" | "unloaded">;
  setState: (s: { id: string; mode: SoundBoxCueMode }) => void;
  cache: CueResolveCache;
  sleep: (ms: number) => Promise<void>;
  maxRetries: number;
  retryMs: number;
}): () => void {
  let cancelled = false;
  if (args.gateText !== null) {
    args.setState({ id: args.itemId, mode: "pending" });
    void resolveSoundBoxCueMode(args.gateText, args.clipState, {
      sleep: args.sleep,
      isCancelled: () => cancelled,
      cache: args.cache,
      maxRetries: args.maxRetries,
      retryMs: args.retryMs,
    }).then((mode) => {
      if (mode !== null && !cancelled) args.setState({ id: args.itemId, mode });
    });
  }
  return () => {
    cancelled = true;
  };
}
